/* ============================================================================
   TENDER ROOM — reactive localStorage store (same pattern as Atelier).
   External singleton + subscription hook. Seeds two demo tenders on first run.
   Every mutation goes through `actions.*`, each of which maps 1:1 onto an
   intended future API route.
   ========================================================================== */
import { useEffect, useState } from "react";
import { notify } from "./notify";
import { emptyItt, normalizeItt } from "./itt";
import { directoryActions } from "./directoryStore";
import type { ParsedSpecs } from "./specsImport";
import type {
  Tender, Bidder, Bid, Rfi, TenderDoc, TimelineEvent, EmailAlert,
  AlertAudience, AlertTrigger, DraftContract, Compliance, Markup,
  InvitationToTender, BidChecklistItem, QuotePackage, QuoteItem, SupplierQuote,
  BidTeamMember, BidTeamRole, DocCategory, ClientSeat, ClientRole, ContractFile,
  ClarificationLetter, SupplierQuery,
} from "./types";

export const APP_URL = "https://studio.studiogy.design";

export const STUDIO = {
  /** The company every report is prepared by. */
  company: "HD STUDIO INC.",
  name: "Atelier",
  unit: "Project Studio",
  manager: "R. Ferdinand",
  managerEmail: "renauto.ferdinand@hddesignstudiogy.com",
  managerCode: "ATELIER",
  tagline: "From the first bid to the final snag.",
};

const KEY = "bidroom.state.v2";

type State = { tenders: Tender[] };

export const uid = (p = "") => p + Math.random().toString(36).slice(2, 9);
const nowISO = () => new Date().toISOString();

/** A supplier's private submission-link token — 32 hex chars, crypto-random. */
export function supplyToken(): string {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  return [...b].map((x) => x.toString(16).padStart(2, "0")).join("");
}

/* ---------------------------------------------------------------- seed -- */
function iso(d: string) { return new Date(d).toISOString(); }

function seedTenders(): Tender[] {
  const t1Bidders: Bidder[] = [
    { id: "bdr-corner", company: "Cornerstone Civil Ltd.", contact: "M. Persaud", email: "tenders@cornerstonecivil.gy", phone: "+592 225 1180", accessCode: "BT-CORNER", invitedAt: iso("2026-07-01T09:00"), status: "submitted", lastActive: iso("2026-07-12T16:40") },
    { id: "bdr-harbor", company: "Harbourline Construction", contact: "A. Chen", email: "bids@harbourline.co", phone: "+592 227 4415", accessCode: "BT-HARBOR", invitedAt: iso("2026-07-01T09:00"), status: "submitted", lastActive: iso("2026-07-13T11:05") },
    { id: "bdr-merid", company: "Meridian Build Group", contact: "S. Ramnarine", email: "estimating@meridianbuild.com", accessCode: "BT-MERID", invitedAt: iso("2026-07-01T09:00"), status: "registered", lastActive: iso("2026-07-10T14:22") },
    { id: "bdr-atlas", company: "Atlas & Cole Contractors", contact: "J. Cole", email: "j.cole@atlascole.build", accessCode: "BT-ATLAS", invitedAt: iso("2026-07-02T10:30"), status: "registered", lastActive: iso("2026-07-11T09:48") },
    { id: "bdr-quarry", company: "Quarry Bay Builders", contact: "T. Ngai", email: "office@quarrybay.build", accessCode: "BT-QUARRY", invitedAt: iso("2026-07-01T09:00"), status: "declined" },
  ];

  const t1: Tender = {
    id: "tdr-breeze",
    code: "TDR-2601",
    name: "Breeze Tower — Main Works",
    location: "Kingston, Georgetown",
    description: "Main works package for a 12-storey mixed-use tower: substructure, superstructure, envelope, fit-out of common areas and external works. Measured contract on re-measurable BOQ.",
    hue: 24,
    status: "open",
    currency: "USD",
    estimate: 4_250_000,
    client: { name: "D. Vetter", company: "Vetter Development Ltd.", email: "d.vetter@vetterdev.com", phone: "+592 610 2244", passcode: "VETTER" },
    issueDate: iso("2026-07-01T09:00"),
    closeDate: iso("2026-07-28T14:00"),
    timeline: [
      { id: "ev-1", label: "Tender issued", date: iso("2026-07-01T09:00"), kind: "issue", note: "Invitations, drawings and BOQ released to all bidders." },
      { id: "ev-2", label: "Pre-bid site visit", date: iso("2026-07-09T10:00"), kind: "site-visit", note: "Assembly at site office, Kingston. Attendance recorded." },
      { id: "ev-3", label: "Addendum 01 issued", date: iso("2026-07-11T15:00"), kind: "addendum", note: "Revised structural GA plans, level 3–5." },
      { id: "ev-4", label: "Last day for RFIs", date: iso("2026-07-18T17:00"), kind: "rfi-deadline" },
      { id: "ev-5", label: "Tender close — bids due", date: iso("2026-07-28T14:00"), kind: "close", note: "Late submissions will not be accepted." },
      { id: "ev-6", label: "Evaluation complete", date: iso("2026-08-04T17:00"), kind: "evaluation" },
      { id: "ev-7", label: "Award notice", date: iso("2026-08-11T12:00"), kind: "award" },
      { id: "ev-8", label: "Contract signing", date: iso("2026-08-25T10:00"), kind: "contract" },
    ],
    docs: [
      { id: "doc-1", number: "A-101", title: "General arrangement plans — L1 to L6", discipline: "Architectural", revision: "P2", category: "drawing", size: "18.4 MB", issuedAt: iso("2026-07-01") },
      { id: "doc-2", number: "A-102", title: "General arrangement plans — L7 to L12", discipline: "Architectural", revision: "P2", category: "drawing", size: "16.1 MB", issuedAt: iso("2026-07-01") },
      { id: "doc-3", number: "A-201", title: "Elevations & sections", discipline: "Architectural", revision: "P1", category: "drawing", size: "9.8 MB", issuedAt: iso("2026-07-01") },
      { id: "doc-4", number: "S-301", title: "Structural GA — foundations & cores", discipline: "Structural", revision: "P3", category: "drawing", size: "12.2 MB", issuedAt: iso("2026-07-11") },
      { id: "doc-5", number: "M-401", title: "Mechanical schematics", discipline: "MEP", revision: "P1", category: "drawing", size: "7.6 MB", issuedAt: iso("2026-07-01") },
      { id: "doc-6", number: "SPEC-03", title: "Specification — Division 03, concrete", discipline: "Commercial", revision: "A", category: "specification", size: "1.9 MB", issuedAt: iso("2026-07-01") },
      { id: "doc-7", number: "SPEC-09", title: "Specification — Division 09, finishes", discipline: "Commercial", revision: "A", category: "specification", size: "2.3 MB", issuedAt: iso("2026-07-01") },
      { id: "doc-8", number: "BOQ-01", title: "Bill of quantities — measured works", discipline: "Commercial", revision: "A", category: "boq", size: "3.4 MB", issuedAt: iso("2026-07-01") },
      { id: "doc-9", number: "FRM-01", title: "Form of Tender (Letter of Tender)", discipline: "Contractual", revision: "A", category: "form", size: "180 KB", issuedAt: iso("2026-07-01") },
      { id: "doc-10", number: "FRM-02", title: "Bid bond form", discipline: "Contractual", revision: "A", category: "form", size: "120 KB", issuedAt: iso("2026-07-01") },
      { id: "doc-11", number: "CON-01", title: "Draft contract — FIDIC Red Book 2017 (Conditions & Particulars)", discipline: "Contractual", revision: "D1", category: "contract", size: "1.1 MB", issuedAt: iso("2026-07-05") },
      { id: "doc-12", number: "ADD-01", title: "Addendum 01 — revised structural GA, L3–L5", discipline: "Structural", revision: "—", category: "addendum", size: "5.5 MB", issuedAt: iso("2026-07-11") },
    ],
    bidders: t1Bidders,
    bids: [
      { id: "bid-corner", bidderId: "bdr-corner", amount: 4_186_500, currency: "USD", durationMonths: 18, submittedAt: iso("2026-07-12T16:40"), bond: true, addendaAck: 1, attachments: ["cornerstone-tender.pdf", "boq-priced.xlsx", "bid-bond.pdf"], compliance: "pending", status: "received", note: "Programme assumes wet-season concrete pours from month 3." },
      { id: "bid-harbor", bidderId: "bdr-harbor", amount: 4_402_000, currency: "USD", durationMonths: 16, submittedAt: iso("2026-07-13T11:05"), bond: true, addendaAck: 1, attachments: ["harbourline-offer.pdf", "priced-boq.xlsx"], compliance: "pending", status: "received" },
    ],
    rfis: [
      { id: "rfi-1", ref: "RFI-001", bidderId: "bdr-corner", subject: "Piling — obstruction allowance", question: "BOQ item 2.14 allows for driven piles to 18 m. Should bidders allow for pre-boring through the old dock slab indicated on S-301, or will this be a variation?", askedAt: iso("2026-07-06T10:12"), answer: "Allow for pre-boring at grid lines A–C as shown on Addendum 01 drawing S-301 P3. Measured under new BOQ item 2.14A.", answeredAt: iso("2026-07-08T15:30"), status: "answered", visibility: "all", docId: "doc-4", markups: [
        { id: "mk-s1", kind: "rect", x: 0.14, y: 0.2, w: 0.24, h: 0.36, note: "Old dock slab zone — pre-bore allowance?" },
        { id: "mk-s2", kind: "pin", x: 0.47, y: 0.63, note: "Confirm pile cap depth at this grid" },
      ] },
      { id: "rfi-2", ref: "RFI-002", bidderId: "bdr-merid", subject: "Provisional sum — lift installation", question: "Is the lift package a nominated subcontract or should bidders price their own supplier against the performance spec?", askedAt: iso("2026-07-10T09:45"), answer: "Bidders price their own supplier against SPEC-14 performance criteria. The provisional sum in BOQ section 11 is deleted by Addendum 01.", answeredAt: iso("2026-07-11T14:00"), status: "answered", visibility: "all" },
      { id: "rfi-3", ref: "RFI-003", bidderId: "bdr-atlas", subject: "Site access hours", question: "Are night pours permitted for the core slipform, given the residential neighbours to the east?", askedAt: iso("2026-07-13T08:20"), status: "open", visibility: "all" },
    ],
    contract: {
      standard: "FIDIC_REDBOOK_2017",
      standardLabel: "FIDIC Red Book — Second Edition 2017",
      employer: "Vetter Development Ltd.",
      contractor: "To be determined by this tender",
      engineer: "HD Studio (the Engineer)",
      timeForCompletion: "18 Months",
      defectsNotification: "365 Days",
      retentionPercent: 10,
      retentionCapPercent: 5,
      performanceSecurityPercent: 10,
      advancePaymentPercent: 15,
      delayDamagesPerDay: { amount: 2_100, currency: "USD" },
      delayDamagesCapPercent: 10,
      paymentPeriodDays: 28,
      governingLaw: "Laws of the Co-operative Republic of Guyana",
      disputeResolution: "DAAB, then ICC arbitration",
      worksDescription: "Main works package — 12-storey mixed-use tower, Kingston, Georgetown.",
      drawingsCount: 5,
      parts: [
        "Letter of Tender",
        "Letter of Acceptance",
        "Contract Agreement (witnessed)",
        "DAAB Agreement",
        "General Conditions — FIDIC 2017 (by reference)",
        "Particular Conditions Part A — Contract Data",
        "Particular Conditions Part B — Special Provisions",
        "Special Conditions of Contract",
        "Appendix A — Drawing Register",
        "Appendix B — Specifications & Schedules",
      ],
      importedAt: iso("2026-07-05T11:20"),
      sourceName: "breeze-tower-main-works.contract.json",
    },
    alerts: [
      { id: "al-1", audience: "bidders", audienceLabel: "All bidders (5)", to: t1Bidders.map(b => b.email), subject: "Invitation to tender — Breeze Tower Main Works (TDR-2601)", body: "You are invited to submit a tender for the Breeze Tower main works package. Documents are available in Bid Studio; your access code is enclosed. Bids close 28 Jul 2026, 14:00.", trigger: "invite", sentAt: iso("2026-07-01T09:05") },
      { id: "al-2", audience: "bidders", audienceLabel: "All bidders (4 active)", to: t1Bidders.filter(b => b.status !== "declined").map(b => b.email), subject: "Addendum 01 issued — revised structural GA (TDR-2601)", body: "Addendum 01 has been issued: revised structural GA plans for levels 3–5 (S-301 P3). Please acknowledge the addendum in your submission.", trigger: "addendum", sentAt: iso("2026-07-11T15:10") },
      { id: "al-3", audience: "client", audienceLabel: "Client — Vetter Development", to: ["d.vetter@vetterdev.com"], subject: "Tender progress — two bids received (TDR-2601)", body: "Two of four active bidders have now submitted. RFI window closes 18 Jul; bids close 28 Jul at 14:00. Evaluation report to follow within one week of close.", trigger: "manual", sentAt: iso("2026-07-13T12:00") },
    ],
    activity: [
      { id: "ac-1", at: iso("2026-07-13T11:05"), who: "Harbourline Construction", what: "submitted a sealed bid", kind: "bid" },
      { id: "ac-2", at: iso("2026-07-13T08:20"), who: "Atlas & Cole Contractors", what: "raised RFI-003 — site access hours", kind: "rfi" },
      { id: "ac-3", at: iso("2026-07-12T16:40"), who: "Cornerstone Civil Ltd.", what: "submitted a sealed bid", kind: "bid" },
      { id: "ac-4", at: iso("2026-07-11T15:10"), who: STUDIO.manager, what: "issued Addendum 01 and notified bidders", kind: "docs" },
      { id: "ac-5", at: iso("2026-07-08T15:30"), who: STUDIO.manager, what: "answered RFI-001 — circulated to all bidders", kind: "rfi" },
      { id: "ac-6", at: iso("2026-07-05T11:20"), who: STUDIO.manager, what: "imported draft contract from Contract Generator", kind: "contract" },
    ],
    resultsSharedWithClient: false,
  };

  const t2Bidders: Bidder[] = [
    { id: "bdr-oak", company: "Oakfield Interiors", contact: "P. Deygoo", email: "bids@oakfieldinteriors.com", accessCode: "ML-OAK", invitedAt: iso("2026-06-16T09:00"), status: "submitted", lastActive: iso("2026-07-08T10:15") },
    { id: "bdr-stell", company: "Stellar Fit-Out Ltd.", contact: "K. Braithwaite", email: "estimating@stellarfitout.com", accessCode: "ML-STELL", invitedAt: iso("2026-06-16T09:00"), status: "submitted", lastActive: iso("2026-07-08T11:40") },
    { id: "bdr-crane", company: "Crane & Marsh Joinery", contact: "R. Marsh", email: "tenders@cranemarsh.co", accessCode: "ML-CRANE", invitedAt: iso("2026-06-16T09:00"), status: "submitted", lastActive: iso("2026-07-07T17:22") },
  ];

  const t2: Tender = {
    id: "tdr-meridian",
    code: "TDR-2602",
    name: "Meridian Loft — Fit-Out Package",
    location: "Tribeca, New York",
    description: "Full interior fit-out of a 480 m² loft residence: joinery, finishes, MEP alterations and decorative lighting. Lump-sum on drawings and finishes schedule.",
    hue: 200,
    status: "evaluation",
    currency: "USD",
    estimate: 910_000,
    client: { name: "L. Osei-Mensah", company: "Meridian Holdings", email: "l.osei@meridianholdings.com", passcode: "MERIDIAN" },
    issueDate: iso("2026-06-16T09:00"),
    closeDate: iso("2026-07-08T12:00"),
    timeline: [
      { id: "ev2-1", label: "Tender issued", date: iso("2026-06-16T09:00"), kind: "issue" },
      { id: "ev2-2", label: "Walk-through — loft", date: iso("2026-06-23T10:00"), kind: "site-visit" },
      { id: "ev2-3", label: "Last day for RFIs", date: iso("2026-06-30T17:00"), kind: "rfi-deadline" },
      { id: "ev2-4", label: "Tender close — bids due", date: iso("2026-07-08T12:00"), kind: "close" },
      { id: "ev2-5", label: "Evaluation complete", date: iso("2026-07-17T17:00"), kind: "evaluation" },
      { id: "ev2-6", label: "Award notice", date: iso("2026-07-21T12:00"), kind: "award" },
      { id: "ev2-7", label: "Contract signing", date: iso("2026-07-31T10:00"), kind: "contract" },
    ],
    docs: [
      { id: "d2-1", number: "ID-100", title: "Fit-out plans & RCPs", discipline: "Interiors", revision: "P1", category: "drawing", size: "11.3 MB", issuedAt: iso("2026-06-16") },
      { id: "d2-2", number: "ID-200", title: "Joinery details & elevations", discipline: "Interiors", revision: "P1", category: "drawing", size: "8.7 MB", issuedAt: iso("2026-06-16") },
      { id: "d2-3", number: "FS-01", title: "Finishes schedule", discipline: "Interiors", revision: "B", category: "specification", size: "1.2 MB", issuedAt: iso("2026-06-16") },
      { id: "d2-4", number: "FRM-01", title: "Form of Tender", discipline: "Contractual", revision: "A", category: "form", size: "160 KB", issuedAt: iso("2026-06-16") },
      { id: "d2-5", number: "CON-01", title: "Draft contract — AIA A101-2017 Agreement", discipline: "Contractual", revision: "D2", category: "contract", size: "640 KB", issuedAt: iso("2026-06-18") },
    ],
    bidders: t2Bidders,
    bids: [
      { id: "bid2-oak", bidderId: "bdr-oak", amount: 862_400, currency: "USD", durationMonths: 7, submittedAt: iso("2026-07-08T10:15"), bond: true, addendaAck: 0, attachments: ["oakfield-proposal.pdf"], compliance: "compliant", score: 88, status: "shortlisted" },
      { id: "bid2-stell", bidderId: "bdr-stell", amount: 897_150, currency: "USD", durationMonths: 6, submittedAt: iso("2026-07-08T11:40"), bond: true, addendaAck: 0, attachments: ["stellar-tender.pdf", "alt-programme.pdf"], compliance: "compliant", score: 84, status: "received", note: "Alternative programme offered at 6 months with weekend working." },
      { id: "bid2-crane", bidderId: "bdr-crane", amount: 941_800, currency: "USD", durationMonths: 8, submittedAt: iso("2026-07-07T17:22"), bond: false, addendaAck: 0, attachments: ["cranemarsh-offer.pdf"], compliance: "non-compliant", score: 61, status: "received", note: "No bid bond enclosed; joinery-only exclusions listed." },
    ],
    rfis: [
      { id: "rfi2-1", ref: "RFI-001", bidderId: "bdr-stell", subject: "Existing services — as-builts", question: "Are as-built MEP drawings available for the existing risers?", askedAt: iso("2026-06-24T09:30"), answer: "Yes — issued to all bidders as ID-090 with the walk-through pack.", answeredAt: iso("2026-06-25T13:00"), status: "answered", visibility: "all" },
    ],
    contract: {
      standard: "AIA_A101_2017",
      standardLabel: "AIA A101-2017 — Owner–Contractor (Stipulated Sum)",
      employer: "Meridian Holdings",
      contractor: "To be determined by this tender",
      engineer: "HD Studio (the Architect)",
      timeForCompletion: "7 Months",
      retentionPercent: 5,
      paymentPeriodDays: 30,
      governingLaw: "State of New York",
      disputeResolution: "Mediation, then arbitration (AAA)",
      worksDescription: "Interior fit-out of the Meridian Loft residence, Tribeca.",
      drawingsCount: 2,
      parts: [
        "A101 Owner–Contractor Agreement",
        "A201 General Conditions (by reference)",
        "Exhibit A — Drawing List",
      ],
      importedAt: iso("2026-06-18T10:00"),
      sourceName: "meridian-loft-fitout.contract.json",
    },
    alerts: [
      { id: "al2-1", audience: "bidders", audienceLabel: "All bidders (3)", to: t2Bidders.map(b => b.email), subject: "Invitation to tender — Meridian Loft fit-out (TDR-2602)", body: "You are invited to tender for the Meridian Loft fit-out. Bids close 8 Jul 2026, 12:00.", trigger: "invite", sentAt: iso("2026-06-16T09:05") },
      { id: "al2-2", audience: "client", audienceLabel: "Client — Meridian Holdings", to: ["l.osei@meridianholdings.com"], subject: "Bids opened — evaluation underway (TDR-2602)", body: "All three bids were opened after the noon close on 8 Jul. Spread is within 9% of estimate. Evaluation report due 17 Jul.", trigger: "results", sentAt: iso("2026-07-08T14:30") },
    ],
    activity: [
      { id: "ac2-1", at: iso("2026-07-10T09:00"), who: STUDIO.manager, what: "shortlisted Oakfield Interiors", kind: "bid" },
      { id: "ac2-2", at: iso("2026-07-08T14:30"), who: STUDIO.manager, what: "opened bids — 3 received, results logged", kind: "bid" },
      { id: "ac2-3", at: iso("2026-07-08T11:40"), who: "Stellar Fit-Out Ltd.", what: "submitted a sealed bid", kind: "bid" },
    ],
    resultsSharedWithClient: true,
  };

  return [t1, t2];
}

/* -------------------------------------------------------------- engine -- */
function load(): State {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* fall through to seed */ }
  return { tenders: seedTenders() };
}

let state: State = load();
const subs = new Set<() => void>();

function commit(next: State) {
  state = next;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
  subs.forEach((f) => f());
}

export function resetStore() { commit({ tenders: seedTenders() }); }

/* --- sync hooks (registered by lib/sync.ts) ---------------------------- */
let tenderListener: ((t: Tender) => void) | null = null;
export function onTenderChange(f: (t: Tender) => void) { tenderListener = f; }
/** Replace state from the server without re-triggering a push. */
export function replaceTenders(tenders: Tender[]) {
  state = { tenders };
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
  subs.forEach((f) => f());
}

export function useStore(): State {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return state;
}

export function useTender(id: string | undefined) {
  return useStore().tenders.find((t) => t.id === id);
}

export function getTenders() { return state.tenders; }

function patchTender(id: string, fn: (t: Tender) => Tender) {
  commit({ tenders: state.tenders.map((t) => (t.id === id ? fn(t) : t)) });
  const changed = state.tenders.find((t) => t.id === id);
  if (changed) tenderListener?.(changed);
}

function logActivity(t: Tender, who: string, what: string, kind: Tender["activity"][number]["kind"]): Tender {
  return { ...t, activity: [{ id: uid("ac-"), at: nowISO(), who, what, kind }, ...t.activity].slice(0, 60) };
}

/** Hard-to-guess access code: PREFIX-XXXXXXXX from an unambiguous alphabet
    (no 0/O/1/I), crypto-random, checked unique across every tender-side code. */
/** Fire-and-forget server-side session revocation for rotated identities.
    Best-effort: offline, the code rotation still lands and blocks new
    sign-ins; the stale sessions die at the server when it's next reachable
    only if retried — so the UI wording promises the code change, not both. */
function revokeSessions(labels: string[]) {
  import("./managerAuth").then(({ authHeader }) =>
    fetch("/api/auth/revoke", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ labels }),
    })
  ).catch(() => { /* best-effort */ });
}

export function secureAccessCode(prefix: string): string {
  const ALPHABET = "23456789ABCDEFGHJKLMNPQRSTUVWXYZ";
  const taken = new Set<string>();
  for (const t of state.tenders) {
    taken.add(t.client.passcode.toUpperCase());
    for (const b of t.bidders) taken.add(b.accessCode.toUpperCase());
    for (const m of t.team ?? []) taken.add(m.accessCode.toUpperCase());
  }
  for (let attempt = 0; attempt < 20; attempt++) {
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    const code = `${prefix.toUpperCase()}-${[...bytes].map((b) => ALPHABET[b % ALPHABET.length]).join("")}`;
    if (!taken.has(code)) return code;
  }
  return `${prefix.toUpperCase()}-${Date.now().toString(36).toUpperCase()}`;
}

/** Patch the real send outcome onto an alert once the worker replies —
    the Alerts page shows Delivered / Simulated / Failed per message. */
function trackDelivery(tid: string, alertId: string, p: Promise<"delivered" | "simulated" | "failed">) {
  void p.then((delivery) => {
    patchTender(tid, (t) => ({ ...t, alerts: t.alerts.map((a) => (a.id === alertId ? { ...a, delivery } : a)) }));
  });
}

/* ------------------------------------------------------------ selectors -- */
/** Every tender where this signed-in tender-side CLIENT belongs — the main
    client rep or a client-team seat, matched by name (and the rep's company).
    Same shape as the project selector; the bid-side portal switcher and its
    route guard both read this. */
export function clientTenders(session: { role?: string; name?: string; company?: string; tenderId?: string; seatId?: string } | null | undefined, tenders: Tender[]): Tender[] {
  if (!session || session.role !== "client") return [];
  const nm = (session.name ?? "").trim().toLowerCase();
  const co = (session.company ?? "").trim().toLowerCase();
  /* Name+company was the original identity, but the same person is routinely
     recorded under a DIFFERENT company on each tender they commission — the
     stable identity across tenders is their email. Resolve it from whichever
     seat (or client-of-record) their code signed them into, then let either
     identity open a tender. */
  const email = clientEmailFor(session, tenders);
  const hit = (n?: string, c?: string) =>
    !!nm && (n ?? "").trim().toLowerCase() === nm && (c ?? "").trim().toLowerCase() === co;
  const mail = (e?: string) => !!email && (e ?? "").trim().toLowerCase() === email;
  return tenders.filter((t) =>
    t.id === session.tenderId ||
    mail(t.client.email) ||
    (t.clientTeam ?? []).some((x) => mail(x.email)) ||
    hit(t.client.name, t.client.company) ||
    (t.clientTeam ?? []).some((x) => hit(x.name, t.client.company)),
  );
}

/** The signed-in client's email, read from the seat (or client of record)
    their access code belongs to on their home tender. */
function clientEmailFor(session: { tenderId?: string; seatId?: string }, tenders: Tender[]): string {
  const home = tenders.find((t) => t.id === session.tenderId);
  if (!home) return "";
  const raw = session.seatId
    ? (home.clientTeam ?? []).find((x) => x.id === session.seatId)?.email
    : home.client.email;
  return (raw ?? "").trim().toLowerCase();
}

/** The role this client holds on a PARTICULAR tender: their own seat's grade
    at home, the matching seat's grade elsewhere, and principal where they are
    the tender's client of record. One person can be an observer on one job
    and the principal on the next — the portal must follow the tender, not the
    session. */
export function clientRoleFor(
  session: { role?: string; name?: string; company?: string; tenderId?: string; seatId?: string; clientRole?: ClientRole } | null | undefined,
  t: Tender, tenders: Tender[],
): ClientRole {
  if (!session || session.role !== "client") return "principal";
  if (t.id === session.tenderId) return session.clientRole ?? "principal";
  const email = clientEmailFor(session, tenders);
  const mail = (e?: string) => !!email && (e ?? "").trim().toLowerCase() === email;
  if (mail(t.client.email)) return "principal";
  const seat = (t.clientTeam ?? []).find((x) => mail(x.email))
    ?? (t.clientTeam ?? []).find((x) => (x.name ?? "").trim().toLowerCase() === (session.name ?? "").trim().toLowerCase());
  return seat?.role ?? session.clientRole ?? "principal";
}

export function isClosed(t: Tender, now = Date.now()) {
  return now > +new Date(t.closeDate) || t.status === "evaluation" || t.status === "awarded";
}
export function bidsUnsealed(t: Tender) {
  if (t.bidMode === "open") return true;   // open tenders never seal — amounts show as they arrive
  return t.status === "evaluation" || t.status === "awarded";
}
export function isSealed(t: Tender) { return t.bidMode !== "open"; }
export function bidderById(t: Tender, id: string) { return t.bidders.find((b) => b.id === id); }
export function submittedCount(t: Tender) { return t.bidders.filter((b) => b.status === "submitted").length; }
export function activeBidders(t: Tender) { return t.bidders.filter((b) => b.status !== "declined"); }
export function openRfis(t: Tender) { return t.rfis.filter((r) => r.status === "open").length; }
export function addendaCount(t: Tender) { return t.docs.filter((d) => d.category === "addendum").length; }
export function lowestBid(t: Tender) { return t.bids.length ? Math.min(...t.bids.map((b) => b.amount)) : undefined; }
export function averageBid(t: Tender) { return t.bids.length ? t.bids.reduce((s, b) => s + b.amount, 0) / t.bids.length : undefined; }
export function rankedBids(t: Tender) { return [...t.bids].sort((a, b) => a.amount - b.amount); }

/** Every tender this signed-in bidder's FIRM is invited to — one access code
    opens all their rooms, matched by company the same way contractors and
    clients reach all their projects. */
export function bidderTenders(session: { role: string; tenderId?: string; company?: string }, tenders: Tender[]): Tender[] {
  if (session.role !== "bidder") return [];
  return tenders.filter((t) =>
    t.id === session.tenderId ||
    t.bidders.some((b) => b.company === session.company && b.status !== "declined"));
}

function resolveAudience(t: Tender, audience: AlertAudience): { label: string; to: string[] } {
  if (audience === "bidders") {
    const act = activeBidders(t);
    return { label: `All bidders (${act.length})`, to: act.map((b) => b.email) };
  }
  if (audience === "client") return { label: `Client — ${t.client.company}`, to: [t.client.email] };
  if (audience === "manager") return { label: `Manager — ${STUDIO.manager}`, to: [STUDIO.managerEmail] };
  const b = bidderById(t, audience);
  return b ? { label: b.company, to: [b.email] } : { label: "—", to: [] };
}

/* -------------------------------------------------------------- actions -- */
const TENDER_HUES = [24, 200, 96, 260, 330, 150];

export const actions = {
  /* --- new tender -------------------------------------------------------- */
  createTender(data: {
    name: string; location: string; description?: string;
    clientName: string; clientCompany: string; clientEmail?: string; clientPhone?: string;
    passcode: string;                       // pre-generated unique client passcode
    currency?: string; estimate?: number;
    closeDate: string;                      // ISO with time — drives the countdown
    siteVisit?: string; rfiDeadline?: string;
    bidMode?: "sealed" | "open";
  }): string {
    const codeNum = state.tenders.length
      ? Math.max(...state.tenders.map((t) => parseInt(t.code.split("-")[1], 10) || 0)) + 1
      : (new Date().getFullYear() % 100) * 100 + 1;
    const code = `TDR-${codeNum}`;
    const issued = nowISO();
    const timeline: TimelineEvent[] = [
      { id: uid("ev-"), label: "Tender issued", date: issued, kind: "issue", note: "Invitations, documents and forms released to bidders." },
      ...(data.siteVisit ? [{ id: uid("ev-"), label: "Pre-bid site visit", date: data.siteVisit, kind: "site-visit" as const }] : []),
      ...(data.rfiDeadline ? [{ id: uid("ev-"), label: "Last day for RFIs", date: data.rfiDeadline, kind: "rfi-deadline" as const }] : []),
      { id: uid("ev-"), label: "Tender close — bids due", date: data.closeDate, kind: "close", note: "Late submissions will not be accepted." },
    ];
    const t: Tender = {
      id: uid("tdr-"), code,
      name: data.name.trim(), location: data.location.trim(),
      description: data.description?.trim() || `${data.name.trim()} — invited tender for ${data.clientCompany.trim()}.`,
      hue: TENDER_HUES[state.tenders.length % TENDER_HUES.length],
      status: "open",
      currency: data.currency || "USD",
      estimate: data.estimate,
      client: { name: data.clientName.trim(), company: data.clientCompany.trim(), email: data.clientEmail?.trim() ?? "", phone: data.clientPhone?.trim(), passcode: data.passcode },
      issueDate: issued,
      closeDate: data.closeDate,
      bidMode: data.bidMode ?? "sealed",
      timeline, docs: [], bidders: [], bids: [], rfis: [], alerts: [],
      activity: [{ id: uid("ac-"), at: issued, who: STUDIO.manager, what: `opened the tender ${code}`, kind: "docs" }],
      resultsSharedWithClient: false,
    };
    commit({ tenders: [t, ...state.tenders] });
    tenderListener?.(t);
    if (t.client.email) {
      notify([t.client.email],
        `Your tender room is open — ${t.name} (${code})`,
        `${STUDIO.name} ${STUDIO.unit}: the tender for ${t.name} is now running. Follow bidding progress in your client room with passcode ${data.passcode}. Bids close ${new Date(data.closeDate).toLocaleString()}.`);
    }
    return t.id;
  },

  /** Move the bid closing time. The countdown, timeline and portals all
      follow; active bidders are formally notified unless opted out. */
  setCloseDate(tid: string, closeDate: string, notifyBidders = true) {
    patchTender(tid, (t) => {
      const before = t.closeDate;
      const extended = +new Date(closeDate) > +new Date(before);
      let next: Tender = {
        ...t,
        closeDate,
        timeline: t.timeline.map((e) => (e.kind === "close" ? { ...e, date: closeDate } : e))
          .sort((a, b) => +new Date(a.date) - +new Date(b.date)),
      };
      if (notifyBidders && activeBidders(t).length) {
        const { label, to } = resolveAudience(t, "bidders");
        const alert: EmailAlert = {
          id: uid("al-"), audience: "bidders", audienceLabel: label, to,
          subject: `${extended ? "Tender deadline extended" : "Tender deadline changed"} — ${t.name} (${t.code})`,
          body: `The closing time for ${t.name} has been ${extended ? "extended" : "brought forward"}. Sealed bids are now due by ${new Date(closeDate).toLocaleString()} (previously ${new Date(before).toLocaleString()}). All other conditions of the tender are unchanged.`,
          trigger: "deadline", sentAt: nowISO(),
        };
        trackDelivery(tid, alert.id, notify(to, alert.subject, alert.body));
        next = { ...next, alerts: [alert, ...next.alerts] };
      }
      return logActivity(next, STUDIO.manager, `moved the bid close to ${new Date(closeDate).toLocaleString()}`, "timeline");
    });
  },

  /* --- alerts ---------------------------------------------------------- */
  sendAlert(tid: string, audience: AlertAudience, subject: string, body: string, trigger: AlertTrigger = "manual") {
    patchTender(tid, (t) => {
      const { label, to } = resolveAudience(t, audience);
      const alert: EmailAlert = { id: uid("al-"), audience, audienceLabel: label, to, subject, body, trigger, sentAt: nowISO() };
      trackDelivery(tid, alert.id, notify(to, subject, body));
      return logActivity({ ...t, alerts: [alert, ...t.alerts] }, STUDIO.manager, `sent alert “${subject}”`, "alert");
    });
  },

  /** Attach a project render (stored in the sheet store) as the tender cover. */
  setCover(tid: string, coverId: string) {
    patchTender(tid, (t) => logActivity({ ...t, coverId }, STUDIO.manager, "updated the project render", "docs"));
  },

  /* --- documents ------------------------------------------------------- */
  addDocument(tid: string, doc: Omit<TenderDoc, "id" | "issuedAt">) {
    patchTender(tid, (t) => {
      const d: TenderDoc = { ...doc, id: uid("doc-"), issuedAt: nowISO() };
      return logActivity({ ...t, docs: [...t.docs, d] }, STUDIO.manager, `added ${d.number} — ${d.title}`, "docs");
    });
  },
  /** Bulk import from a scanned drawing set: existing numbers are revised in
      place (the tender register keeps one current row per sheet), new numbers
      are added as drawings. A sheet whose scanned revision MATCHES the row is
      a re-attach (same issue, now with the real drawing) — the rev stays. */
  importDrawings(tid: string, rows: { number: string; title: string; discipline: string; revision?: string; size?: string; category?: DocCategory; previewId?: string; fileId?: string; fileName?: string; filePage?: number; pageFileId?: string }[], sourceLabel = "a drawing-set scan") {
    const bump = (rev: string) => { const m = rev.match(/^([A-Za-z-]*?)(\d+)$/); return m ? m[1] + (+m[2] + 1) : rev + "-1"; };
    patchTender(tid, (t) => {
      const docs = [...t.docs];
      // Match on letters+digits only, so "H-100" and "H100" are recognised as the
      // SAME sheet and land as a revision rather than a second card in the set.
      const key = (n: string) => n.toUpperCase().replace(/[^A-Z0-9]/g, "");
      for (const r of rows) {
        const i = docs.findIndex((d) => (d.category === "drawing" || d.category === "specification") && key(d.number) === key(r.number));
        if (i >= 0) {
          const rev = !r.revision ? bump(docs[i].revision)
            : r.revision.toUpperCase() === docs[i].revision.toUpperCase() ? docs[i].revision
            : r.revision;
          docs[i] = { ...docs[i], title: r.title || docs[i].title, discipline: r.discipline || docs[i].discipline, revision: rev, size: r.size ?? docs[i].size, issuedAt: nowISO(), previewId: r.previewId ?? docs[i].previewId, fileId: r.fileId ?? docs[i].fileId, fileName: r.fileName ?? docs[i].fileName, filePage: r.filePage ?? docs[i].filePage, pageFileId: r.fileId ? r.pageFileId : (r.pageFileId ?? docs[i].pageFileId) };
        } else {
          docs.push({ id: uid("doc-"), number: r.number, title: r.title, discipline: r.discipline, revision: r.revision || "P1", category: r.category ?? "drawing", size: r.size ?? "—", issuedAt: nowISO(), previewId: r.previewId, fileId: r.fileId, fileName: r.fileName, filePage: r.filePage, pageFileId: r.pageFileId });
        }
      }
      /* Heal pass: sheets can miss their downloadable original after a failed
         upload, and a later re-scan may name pages differently — so number
         matching alone strands them forever. The source FILE is the ground
         truth: any card that recorded this fileName but has no stored copy
         gets this upload's fileId attached (its filePage already points at
         the right page inside it). */
      const byName = new Map<string, string>();
      for (const r of rows) if (r.fileName && r.fileId) byName.set(r.fileName, r.fileId);
      let healed = 0;
      const healedDocs = docs.map((d) => {
        const fid = !d.fileId && d.fileName ? byName.get(d.fileName) : undefined;
        if (fid) { healed++; return { ...d, fileId: fid }; }
        return d;
      });
      const note = `imported ${rows.length} sheet${rows.length === 1 ? "" : "s"} from ${sourceLabel}${healed ? ` · attached originals to ${healed} earlier sheet${healed === 1 ? "" : "s"}` : ""}`;
      /* Every active bidder is pricing against this register — a set that
         changes silently is how a bid misses an addendum-sized change. One
         batched email per import, not one per sheet. */
      let next: Tender = { ...t, docs: healedDocs };
      if (rows.length && activeBidders(t).length && !t.awardedBidderId) {
        const revisedCount = rows.filter((r) =>
          t.docs.some((d) => (d.category === "drawing" || d.category === "specification") && key(d.number) === key(r.number))).length;
        const newCount = rows.length - revisedCount;
        const summary = [
          newCount ? `${newCount} new sheet${newCount === 1 ? "" : "s"}` : "",
          revisedCount ? `${revisedCount} revised sheet${revisedCount === 1 ? "" : "s"}` : "",
        ].filter(Boolean).join(" and ");
        const { label: audLabel, to } = resolveAudience(t, "bidders");
        const alert: EmailAlert = {
          id: uid("al-"), audience: "bidders", audienceLabel: audLabel, to,
          subject: `Tender documents updated — ${summary} (${t.code})`,
          body: `${t.name}: the tender document register has been updated — ${summary}.\n\n` +
            rows.slice(0, 12).map((r) => `  • ${r.number} — ${r.title}`).join("\n") +
            (rows.length > 12 ? `\n  …and ${rows.length - 12} more` : "") +
            `\n\nOpen your bidder room → Documents for the current set, and price against the latest revisions.`,
          trigger: "addendum", sentAt: nowISO(),   // register-change notices ride the addendum channel
        };
        trackDelivery(tid, alert.id, notify(to, alert.subject, alert.body));
        next = { ...next, alerts: [alert, ...next.alerts] };
      }
      return logActivity(next, STUDIO.manager, note, "docs");
    });
  },
  /** Store a preview that was rebuilt from the original, so the repair is done
      once for everyone rather than re-rendered in every visitor's browser. */
  setDocPreview(tid: string, docId: string, previewId: string) {
    patchTender(tid, (t) => ({
      ...t,
      docs: t.docs.map((d) => (d.id === docId ? { ...d, previewId } : d)),
    }));
  },

  /** Attach a sheet's own one-page PDF, split from the combined original —
      done once by the studio, so every download and every phone gets the
      small file instead of the whole set. */
  setDocPageFile(tid: string, docId: string, pageFileId: string) {
    patchTender(tid, (t) => ({
      ...t,
      docs: t.docs.map((d) => (d.id === docId ? { ...d, pageFileId } : d)),
    }));
  },

  /** Fold sheets that are the same drawing under different spellings ("H-100"
      and "H100") into one card, keeping the richest copy. Returns how many went. */
  mergeDuplicateSheets(tid: string): number {
    let removed = 0;
    patchTender(tid, (t) => {
      const key = (n: string) => n.toUpperCase().replace(/[^A-Z0-9]/g, "");
      const best = new Map<string, TenderDoc>();
      for (const d of t.docs) {
        const k = d.category === "drawing" || d.category === "specification" ? key(d.number) : `id:${d.id}`;
        const prev = best.get(k);
        if (!prev) { best.set(k, d); continue; }
        removed++;
        // keep whichever copy carries more — a preview and an original beat neither
        const score = (x: TenderDoc) => (x.previewId ? 2 : 0) + (x.fileId ? 1 : 0);
        const winner = score(d) > score(prev) ? d : prev;
        const loser = winner === d ? prev : d;
        best.set(k, { ...winner, previewId: winner.previewId ?? loser.previewId, fileId: winner.fileId ?? loser.fileId, fileName: winner.fileName ?? loser.fileName, filePage: winner.filePage ?? loser.filePage });
      }
      if (!removed) return t;
      return logActivity({ ...t, docs: [...best.values()] }, STUDIO.manager,
        `merged ${removed} duplicate sheet${removed === 1 ? "" : "s"} in the register`, "docs");
    });
    return removed;
  },

  /** Remove a document from the tender register — Tender Admin only (UI). */
  /** Drop drawing/spec cards that have neither a preview nor a stored file —
      the artefacts of failed imports. Returns how many went. */
  purgePlaceholderDocs(tid: string): number {
    let removed = 0;
    patchTender(tid, (t) => {
      const keep = t.docs.filter((d) => !((d.category === "drawing" || d.category === "specification") && !d.previewId && !d.fileId));
      removed = t.docs.length - keep.length;
      if (!removed) return t;
      return logActivity({ ...t, docs: keep }, STUDIO.manager, `purged ${removed} empty placeholder sheet${removed === 1 ? "" : "s"}`, "docs");
    });
    return removed;
  },

  removeDocument(tid: string, docId: string) {
    patchTender(tid, (t) => {
      const d = t.docs.find((x) => x.id === docId);
      if (!d) return t;
      return logActivity(
        { ...t, docs: t.docs.filter((x) => x.id !== docId) },
        STUDIO.manager, `removed ${d.number} — ${d.title} from the register`, "docs");
    });
  },

  /** Rotate the shared client passcode: a fresh code is minted, the old one
      stops opening the portal, every session that identity holds is revoked
      server-side, and the client is emailed the new key. The old code is
      never shown again — that is the point of a rotation. */
  rotateClientPasscode(tid: string): string {
    let fresh = "";
    patchTender(tid, (t) => {
      fresh = secureAccessCode(t.code.split("-")[1]?.slice(0, 2) ?? "CL");
      notify([t.client.email].filter(Boolean),
        `Your access code was changed — ${t.name} (${t.code})`,
        `For security, your client portal access code for ${t.name} has been changed.\n\n` +
        `New code: ${fresh}\n\nThe previous code no longer works, and any signed-in sessions have been ended. ` +
        `Sign in again at https://studio.studiogy.design with the new code.`);
      revokeSessions([`client:${t.client.name}`]);
      return logActivity(
        { ...t, client: { ...t.client, passcode: fresh } },
        STUDIO.manager, `rotated the client portal passcode for ${t.client.name}`, "alert");
    });
    return fresh;
  },
  /** Extend the scope of works. The tender's own scope text grows, and —
      because bidders priced the OLD scope — the extension goes out as a
      numbered addendum they must acknowledge. A scope that changes without an
      addendum is a dispute at award; this makes the formal path the easy one. */
  extendScope(tid: string, addition: string, asAddendum = true) {
    const added = addition.trim();
    if (!added) return;
    patchTender(tid, (t) => logActivity(
      {
        ...t,
        description: [t.description?.trim(), added].filter(Boolean).join("\n\n"),
        // the ITT's own Scope of Works section, when the pack has one
        itt: t.itt
          ? {
              ...t.itt,
              sections: t.itt.sections.map((sec) =>
                sec.key === "scope" ? { ...sec, body: [sec.body?.trim(), added].filter(Boolean).join("\n\n") } : sec),
            }
          : t.itt,
      },
      STUDIO.manager, "extended the scope of works", "docs"));
    if (asAddendum) actions.issueAddendum(tid, "Scope of works extended", "General", added);
  },

  issueAddendum(tid: string, title: string, discipline: string, note: string) {
    patchTender(tid, (t) => {
      const n = t.docs.filter((d) => d.category === "addendum").length + 1;
      const num = `ADD-${String(n).padStart(2, "0")}`;
      const doc: TenderDoc = { id: uid("doc-"), number: num, title: `Addendum ${String(n).padStart(2, "0")} — ${title}`, discipline, revision: "—", category: "addendum", size: "—", issuedAt: nowISO(), note: note?.trim() || undefined };
      const ev: TimelineEvent = { id: uid("ev-"), label: `Addendum ${String(n).padStart(2, "0")} issued`, date: nowISO(), kind: "addendum", note: title };
      const { label, to } = resolveAudience(t, "bidders");
      const alert: EmailAlert = {
        id: uid("al-"), audience: "bidders", audienceLabel: label, to,
        subject: `Addendum ${String(n).padStart(2, "0")} issued — ${title} (${t.code})`,
        body: `${note || title} Please acknowledge this addendum in your submission.`,
        trigger: "addendum", sentAt: nowISO(),
      };
      trackDelivery(tid, alert.id, notify(to, alert.subject, alert.body));
      let next: Tender = { ...t, docs: [...t.docs, doc], timeline: [...t.timeline, ev].sort((a, b) => +new Date(a.date) - +new Date(b.date)), alerts: [alert, ...t.alerts] };
      return logActivity(next, STUDIO.manager, `issued ${num} and notified bidders`, "docs");
    });
  },
  /* --- bid clarifications -------------------------------------------------- */
  /** Issue a formal clarification letter to ONE bidder during evaluation.
      Ref runs CL-01, CL-02… per tender; the letter is emailed with delivery
      tracking and the exchange lives on the bid record. */
  issueClarification(tid: string, bidId: string, data: { subject: string; body: string; respondBy?: string }) {
    patchTender(tid, (t) => {
      const bid = t.bids.find((x) => x.id === bidId);
      const bdr = bid && bidderById(t, bid.bidderId);
      if (!bid || !bdr) return t;
      const n = t.bids.reduce((s, b) => s + (b.clarifications?.length ?? 0), 0) + 1;
      const letter: ClarificationLetter = {
        id: uid("cl-"), ref: `CL-${String(n).padStart(2, "0")}`,
        subject: data.subject.trim(), body: data.body.trim(),
        respondBy: data.respondBy || undefined, issuedAt: nowISO(),
      };
      const alert: EmailAlert = {
        id: uid("al-"), audience: bdr.id, audienceLabel: bdr.company, to: [bdr.email],
        subject: `${letter.ref} — Bid clarification required · ${t.name} (${t.code})`,
        body: `${STUDIO.name} ${STUDIO.unit}\n\n${letter.body}\n\n` +
          (letter.respondBy ? `Please respond by ${letter.respondBy} through your bidder room.\n\n` : "Please respond through your bidder room.\n\n") +
          `Sign in at ${APP_URL} — the letter and a reply box are on your Your Bid tab. Your reply forms part of the tender record.`,
        trigger: "clarification", sentAt: nowISO(),
      };
      trackDelivery(tid, alert.id, notify([bdr.email], alert.subject, alert.body));
      const next: Tender = {
        ...t,
        bids: t.bids.map((b) => b.id === bidId ? { ...b, clarifications: [...(b.clarifications ?? []), letter] } : b),
        alerts: [alert, ...t.alerts],
      };
      return logActivity(next, STUDIO.manager, `issued ${letter.ref} — bid clarification to ${bdr.company}`, "docs");
    });
  },

  /** The bidder's answer — stored on the letter, manager notified. */
  respondClarification(tid: string, bidId: string, clarId: string, text: string) {
    patchTender(tid, (t) => {
      const bid = t.bids.find((x) => x.id === bidId);
      const bdr = bid && bidderById(t, bid.bidderId);
      const letter = bid?.clarifications?.find((c) => c.id === clarId);
      if (!bid || !bdr || !letter || letter.response) return t;
      void notify(STUDIO.managerEmail,
        `${letter.ref} answered — ${bdr.company} · ${t.code}`,
        `${bdr.company} responded to ${letter.ref} (${letter.subject}):\n\n${text.trim()}\n\nReview it on the Bid Results page.`);
      const next: Tender = {
        ...t,
        bids: t.bids.map((b) => b.id === bidId
          ? { ...b, clarifications: (b.clarifications ?? []).map((c) => c.id === clarId ? { ...c, response: text.trim(), respondedAt: nowISO() } : c) }
          : b),
      };
      return logActivity(next, bdr.company, `responded to ${letter.ref}`, "docs");
    });
  },

  /* --- bid manager team --------------------------------------------------- */
  inviteBidTeam(tid: string, name: string, email: string, role: BidTeamRole, presetCode: string) {
    patchTender(tid, (t) => {
      const m: BidTeamMember = { id: uid("btm-"), name, email, role, accessCode: presetCode, invitedAt: nowISO(), status: "invited" };
      const roleLabel = role === "admin" ? "Tender Admin" : role === "coordinator" ? "Tender Coordinator" : "Viewer";
      const alert: EmailAlert = {
        id: uid("al-"), audience: "team", audienceLabel: `${name} (team)`, to: [email],
        subject: `You've been added to the tender team — ${t.name} (${t.code})`,
        body: `${STUDIO.name}: you're on the team running the tender for ${t.name} as ${roleLabel}.\n\n` +
          `Your access code: ${presetCode}\nSign in at ${APP_URL} — tap "Sign in" and enter the code. The code is personal to you.`,
        trigger: "invite", sentAt: nowISO(),
      };
      trackDelivery(tid, alert.id, notify([email], alert.subject, alert.body));
      return logActivity({ ...t, team: [...(t.team ?? []), m], alerts: [alert, ...t.alerts] }, STUDIO.manager, `added ${name} to the tender team as ${roleLabel}`, "bidder");
    });
  },
  setBidTeamRole(tid: string, memberId: string, role: BidTeamRole) {
    patchTender(tid, (t) => ({ ...t, team: (t.team ?? []).map((m) => (m.id === memberId ? { ...m, role } : m)) }));
  },
  /** New unique code; the old one stops working immediately. Emails the member. */
  reissueBidTeamCode(tid: string, memberId: string, newCode: string) {
    patchTender(tid, (t) => {
      const m = (t.team ?? []).find((x) => x.id === memberId);
      if (!m) return t;
      notify([m.email], `Your access code was reissued — ${t.name} (${t.code})`,
        `${STUDIO.name}: your access code for the ${t.name} tender team is now ${newCode}. The previous code no longer works.\nSign in at ${APP_URL}.`);
      return logActivity({ ...t, team: (t.team ?? []).map((x) => (x.id === memberId ? { ...x, accessCode: newCode } : x)) }, STUDIO.manager, `reissued ${m.name}'s team access code`, "bidder");
    });
  },
  removeBidTeamMember(tid: string, memberId: string) {
    patchTender(tid, (t) => {
      const m = (t.team ?? []).find((x) => x.id === memberId);
      const next: Tender = { ...t, team: (t.team ?? []).filter((x) => x.id !== memberId) };
      return m ? logActivity(next, STUDIO.manager, `removed ${m.name} from the tender team`, "bidder") : next;
    });
  },
  markBidTeamActive(tid: string, memberId: string) {
    patchTender(tid, (t) => ({
      ...t,
      team: (t.team ?? []).map((m) => (m.id === memberId ? { ...m, status: "active" as const, lastActive: nowISO() } : m)),
    }));
  },

  /* --- client-side team --------------------------------------------------- */
  inviteClientSeat(tid: string, name: string, email: string, role: ClientRole, presetCode: string) {
    patchTender(tid, (t) => {
      const seat: ClientSeat = { id: uid("cs-"), name, email, role, accessCode: presetCode, invitedAt: nowISO(), status: "invited" };
      const roleLabel = role === "principal" ? "Principal" : role === "commercial" ? "Commercial" : "Observer";
      const alert: EmailAlert = {
        id: uid("al-"), audience: "client-team", audienceLabel: `${name} (client team)`, to: [email],
        subject: `Your client portal access — ${t.name} (${t.code})`,
        body: `${STUDIO.name}: you've been given ${roleLabel} access to the client portal for the ${t.name} tender.\n\n` +
          `Your access code: ${presetCode}\nSign in at ${APP_URL} — the code is personal to you.`,
        trigger: "invite", sentAt: nowISO(),
      };
      trackDelivery(tid, alert.id, notify([email], alert.subject, alert.body));
      return logActivity({ ...t, clientTeam: [...(t.clientTeam ?? []), seat], alerts: [alert, ...t.alerts] }, STUDIO.manager, `added ${name} to the client team as ${roleLabel}`, "bidder");
    });
  },
  setClientSeatRole(tid: string, seatId: string, role: ClientRole) {
    patchTender(tid, (t) => ({ ...t, clientTeam: (t.clientTeam ?? []).map((s) => (s.id === seatId ? { ...s, role } : s)) }));
  },
  reissueClientSeatCode(tid: string, seatId: string, newCode: string) {
    patchTender(tid, (t) => {
      const s = (t.clientTeam ?? []).find((x) => x.id === seatId);
      if (!s) return t;
      notify([s.email], `Your access code was updated — ${t.name} (${t.code})`,
        `${STUDIO.name}: your client portal access code for ${t.name} is now ${newCode}. The previous code no longer works.\nSign in at ${APP_URL}.`);
      // rotation is only real if devices already signed in fall out too
      revokeSessions([`client:${s.name}`]);
      return logActivity({ ...t, clientTeam: (t.clientTeam ?? []).map((x) => (x.id === seatId ? { ...x, accessCode: newCode } : x)) }, STUDIO.manager, `reissued ${s.name}'s client access code`, "bidder");
    });
  },
  removeClientSeat(tid: string, seatId: string) {
    patchTender(tid, (t) => {
      const s = (t.clientTeam ?? []).find((x) => x.id === seatId);
      const next: Tender = { ...t, clientTeam: (t.clientTeam ?? []).filter((x) => x.id !== seatId) };
      return s ? logActivity(next, STUDIO.manager, `removed ${s.name} from the client team`, "bidder") : next;
    });
  },
  markClientSeatActive(tid: string, seatId: string) {
    patchTender(tid, (t) => ({
      ...t,
      clientTeam: (t.clientTeam ?? []).map((s) => (s.id === seatId ? { ...s, status: "active" as const, lastActive: nowISO() } : s)),
    }));
  },

  /* --- supply quotation packages (RFQs) ---------------------------------- */
  createRfq(tid: string, data: { title: string; scope?: string; closeDate?: string; items: Omit<QuoteItem, "id">[]; docIds?: string[]; inviteLetter?: string }) {
    patchTender(tid, (t) => {
      const n = (t.rfqs ?? []).length + 1;
      const rfq: QuotePackage = {
        id: uid("rfq-"), ref: `RFQ-${String(n).padStart(2, "0")}`,
        title: data.title.trim(), scope: data.scope?.trim() || undefined, closeDate: data.closeDate,
        inviteLetter: data.inviteLetter?.trim() || undefined,
        items: data.items.map((x) => ({ ...x, id: uid("qi-") })),
        docIds: data.docIds?.length ? data.docIds : undefined,
        issuedTo: [], quotes: [], status: "draft",
      };
      return logActivity({ ...t, rfqs: [rfq, ...(t.rfqs ?? [])] }, STUDIO.manager, `opened quotation package ${rfq.ref} — ${rfq.title}`, "docs");
    });
  },
  deleteRfq(tid: string, rfqId: string) {
    patchTender(tid, (t) => ({ ...t, rfqs: (t.rfqs ?? []).filter((r) => r.id !== rfqId) }));
  },
  /** Email the package to suppliers and mark it issued. */
  issueRfq(tid: string, rfqId: string, recipients: { name: string; email: string }[]) {
    patchTender(tid, (t) => {
      const rfq = (t.rfqs ?? []).find((r) => r.id === rfqId);
      if (!rfq || !recipients.length) return t;
      // each supplier gets a private submission token — their /quote/<token>
      // page shows the package and takes their quote directly
      const withTokens = recipients.map((s) => {
        const existing = rfq.issuedTo.find((x) => x.name.toLowerCase() === s.name.toLowerCase());
        return { ...s, token: existing?.token ?? supplyToken() };
      });
      // the directory learns every supplier this scope goes to
      for (const s of recipients) {
        directoryActions.learn({ name: s.name, email: s.email, kind: "supplier", scopeTitle: rfq.title, event: { role: "rfq-issued", ref: rfq.ref, label: rfq.title } });
      }
      const lines = rfq.items.map((x, i) =>
        `  ${i + 1}. ${x.description}${x.qty ? ` — ${x.qty} ${x.unit ?? ""}`.trimEnd() : ""}`).join("\n");
      const subject = `Invitation to price ${rfq.ref} — ${rfq.title} (${t.name})`;
      // the formal Invitation-to-Price letter leads when one is on the package
      const opening = rfq.inviteLetter?.trim()
        ? rfq.inviteLetter.trim()
        : `${STUDIO.name} invites your quotation for the following supply package on ${t.name}, ${t.location}, ` +
          `priced separately from the main building tender.\n\n${rfq.title}${rfq.scope ? `\n${rfq.scope}` : ""}`;
      const bodyFor = (token: string) =>
        `${opening}\n\nItems to price:\n${lines}\n\n` +
        (rfq.closeDate ? `Quotations are due by ${new Date(rfq.closeDate).toLocaleDateString()}.\n\n` : "\n") +
        `SUBMIT YOUR QUOTATION HERE: ${APP_URL}/quote/${token}\n` +
        `Your private portal shows the full package — drawings included — and takes your price, lead time, validity and quotation document directly. ` +
        `Please submit through the portal; quotations sent by email reply cannot be accepted into the comparison. ` +
        `Questions are welcome by reply — clarifications of general relevance are circulated to all invited suppliers.`;
      const to = withTokens.map((r) => r.email).filter((e) => /\S+@\S+/.test(e));
      // per-recipient sends — each email carries that supplier's own link
      const sends = withTokens
        .filter((r) => /\S+@\S+/.test(r.email))
        .map((r) => notify([r.email], subject, bodyFor(r.token!)));
      const alert: EmailAlert = {
        id: uid("al-"), audience: "suppliers", audienceLabel: `Suppliers (${withTokens.length}) — ${rfq.ref}`,
        to, subject, body: bodyFor("<personal link per supplier>"), trigger: "quote", sentAt: nowISO(),
      };
      if (sends.length) trackDelivery(tid, alert.id, sends[0]);
      const next: Tender = {
        ...t,
        rfqs: (t.rfqs ?? []).map((r) => r.id === rfqId
          ? { ...r, status: "issued" as const, issuedAt: nowISO(), issuedTo: [...r.issuedTo.filter((x) => !withTokens.some((n) => n.name.toLowerCase() === x.name.toLowerCase())), ...withTokens] }
          : r),
        alerts: [alert, ...t.alerts],
      };
      return logActivity(next, STUDIO.manager, `issued ${rfq.ref} to ${recipients.length} supplier${recipients.length === 1 ? "" : "s"}`, "alert");
    });
  },
  /** Save/replace the Invitation-to-Price letter on a package. */
  setInviteLetter(tid: string, rfqId: string, text: string) {
    patchTender(tid, (t) => ({
      ...t,
      rfqs: (t.rfqs ?? []).map((r) => r.id === rfqId ? { ...r, inviteLetter: text.trim() || undefined } : r),
    }));
  },

  /* --- supplier clarifications (the RFQ mirror of tender RFIs) ------------- */
  /** Record a question — a supplier's (received by email/WhatsApp) or the
      studio's own request TO a supplier (usually about their quote, which is
      emailed to them immediately). */
  addRfqQuery(tid: string, rfqId: string, data: { supplier: string; question: string; direction: SupplierQuery["direction"] }) {
    patchTender(tid, (t) => {
      const rfq = (t.rfqs ?? []).find((r) => r.id === rfqId);
      if (!rfq) return t;
      const ref = `Q-${String((rfq.queries?.length ?? 0) + 1).padStart(2, "0")}`;
      const q: SupplierQuery = { id: uid("qq-"), ref, supplier: data.supplier.trim(), direction: data.direction, question: data.question.trim(), askedAt: nowISO() };
      if (data.direction === "to-supplier") {
        const email = rfq.issuedTo.find((s) => s.name.toLowerCase() === q.supplier.toLowerCase())?.email
          ?? rfq.quotes.find((x) => x.supplier.toLowerCase() === q.supplier.toLowerCase())?.email;
        if (email) {
          void notify([email],
            `${rfq.ref} ${ref} — clarification requested · ${rfq.title} (${t.code})`,
            `${STUDIO.name} ${STUDIO.unit}: regarding ${rfq.ref} — ${rfq.title}.\n\n${q.question}\n\nPlease reply to this email referencing ${ref}. Your reply forms part of the quotation record.`);
        }
      }
      const next: Tender = { ...t, rfqs: (t.rfqs ?? []).map((r) => r.id === rfqId ? { ...r, queries: [...(r.queries ?? []), q] } : r) };
      return logActivity(next, data.direction === "from-supplier" ? q.supplier : STUDIO.manager,
        data.direction === "from-supplier" ? `asked ${ref} on ${rfq.ref}` : `requested clarification ${ref} from ${q.supplier} on ${rfq.ref}`, "rfi");
    });
  },

  /** Answer a query. `circulate` emails the answer to EVERY issued supplier
      (anonymised) — the supply-side clarification; otherwise just the asker. */
  answerRfqQuery(tid: string, rfqId: string, queryId: string, answer: string, circulate: boolean) {
    patchTender(tid, (t) => {
      const rfq = (t.rfqs ?? []).find((r) => r.id === rfqId);
      const q = rfq?.queries?.find((x) => x.id === queryId);
      if (!rfq || !q || q.answer) return t;
      const askerEmail = rfq.issuedTo.find((s) => s.name.toLowerCase() === q.supplier.toLowerCase())?.email;
      const to = circulate ? [...new Set(rfq.issuedTo.map((s) => s.email).filter(Boolean))] : askerEmail ? [askerEmail] : [];
      if (to.length) {
        const alert: EmailAlert = {
          id: uid("al-"), audience: "suppliers", audienceLabel: circulate ? `Suppliers (${to.length}) — ${rfq.ref}` : q.supplier,
          to,
          subject: `${rfq.ref} ${q.ref} clarification — ${rfq.title} (${t.code})`,
          body: circulate
            ? `Clarification issued to all invited suppliers on ${rfq.ref} — ${rfq.title}.\n\nQuestion raised: ${q.question}\n\nAnswer: ${answer.trim()}\n\nAll other terms of the invitation are unchanged.`
            : `Regarding your question ${q.ref} on ${rfq.ref} — ${rfq.title}:\n\n${answer.trim()}`,
          trigger: "quote", sentAt: nowISO(),
        };
        trackDelivery(tid, alert.id, notify(to, alert.subject, alert.body));
        const next: Tender = {
          ...t,
          rfqs: (t.rfqs ?? []).map((r) => r.id === rfqId
            ? { ...r, queries: (r.queries ?? []).map((x) => x.id === queryId ? { ...x, answer: answer.trim(), answeredAt: nowISO(), circulated: circulate } : x) }
            : r),
          alerts: [alert, ...t.alerts],
        };
        return logActivity(next, STUDIO.manager, `answered ${q.ref} on ${rfq.ref}${circulate ? " — circulated to all suppliers" : ""}`, "rfi");
      }
      const next: Tender = {
        ...t,
        rfqs: (t.rfqs ?? []).map((r) => r.id === rfqId
          ? { ...r, queries: (r.queries ?? []).map((x) => x.id === queryId ? { ...x, answer: answer.trim(), answeredAt: nowISO(), circulated: circulate } : x) }
          : r),
      };
      return logActivity(next, STUDIO.manager, `answered ${q.ref} on ${rfq.ref}`, "rfi");
    });
  },

  recordQuote(tid: string, rfqId: string, data: Omit<SupplierQuote, "id" | "receivedAt" | "selected">) {
    patchTender(tid, (t) => {
      const q: SupplierQuote = { ...data, id: uid("sq-"), receivedAt: nowISO() };
      const next: Tender = { ...t, rfqs: (t.rfqs ?? []).map((r) => r.id === rfqId ? { ...r, quotes: [...r.quotes, q] } : r) };
      const rfq = (t.rfqs ?? []).find((r) => r.id === rfqId);
      if (rfq) directoryActions.learn({ name: data.supplier, email: data.email, kind: "supplier", scopeTitle: rfq.title, event: { role: "quoted", ref: rfq.ref, label: rfq.title } });
      return logActivity(next, STUDIO.manager, `recorded ${data.supplier}'s quote on ${rfq?.ref ?? "an RFQ"}`, "bid");
    });
  },
  /** Mark the winning quote; optionally emails the supplier. */
  selectQuote(tid: string, rfqId: string, quoteId: string) {
    patchTender(tid, (t) => {
      const _rfq = (t.rfqs ?? []).find((r) => r.id === rfqId);
      const _q = _rfq?.quotes.find((x) => x.id === quoteId);
      if (_rfq && _q) directoryActions.learn({ name: _q.supplier, email: _q.email, kind: "supplier", scopeTitle: _rfq.title, event: { role: "quote-won", ref: _rfq.ref, label: _rfq.title } });
      const rfq = (t.rfqs ?? []).find((r) => r.id === rfqId);
      const q = rfq?.quotes.find((x) => x.id === quoteId);
      if (!rfq || !q) return t;
      if (q.email) {
        notify([q.email], `Quotation accepted — ${rfq.ref} ${rfq.title} (${t.name})`,
          `${STUDIO.name}: your quotation for ${rfq.title} on ${t.name} has been selected. A purchase order / supply agreement follows.`);
      }
      const next: Tender = {
        ...t,
        rfqs: (t.rfqs ?? []).map((r) => r.id === rfqId
          ? { ...r, status: "awarded" as const, quotes: r.quotes.map((x) => ({ ...x, selected: x.id === quoteId })) }
          : r),
      };
      return logActivity(next, STUDIO.manager, `selected ${q.supplier} on ${rfq.ref} — ${rfq.title}`, "award");
    });
  },

  /* --- timeline --------------------------------------------------------- */
  addTimelineEvent(tid: string, ev: Omit<TimelineEvent, "id">) {
    patchTender(tid, (t) =>
      logActivity({ ...t, timeline: [...t.timeline, { ...ev, id: uid("ev-") }].sort((a, b) => +new Date(a.date) - +new Date(b.date)) }, STUDIO.manager, `added “${ev.label}” to the programme`, "timeline"));
  },

  /* --- bidders ----------------------------------------------------------- */
  inviteBidder(tid: string, company: string, contact: string, email: string, presetCode?: string) {
    patchTender(tid, (t) => {
      // the directory learns every contractor the studio tenders to
      directoryActions.learn({ name: company, email, kind: "contractor", scopeTitle: t.name, event: { role: "tender-invited", ref: t.code, label: t.name } });
      const code = presetCode ?? secureAccessCode(t.code.split("-")[1]?.slice(0, 2) ?? "TR");
      const b: Bidder = { id: uid("bdr-"), company, contact, email, accessCode: code, invitedAt: nowISO(), status: "invited" };
      const alert: EmailAlert = {
        id: uid("al-"), audience: b.id, audienceLabel: company, to: [email],
        subject: `Invitation to tender — ${t.name} (${t.code})`,
        body: `You are invited to submit a tender for ${t.name}. Your access code is ${code}. Bids close ${new Date(t.closeDate).toLocaleString()}.\n\nSign in to your bidder room at ${APP_URL} — the drawings, invitation and submission checklist are all there.`,
        trigger: "invite", sentAt: nowISO(),
      };
      trackDelivery(tid, alert.id, notify([email], alert.subject, alert.body));
      return logActivity({ ...t, bidders: [...t.bidders, b], alerts: [alert, ...t.alerts] }, STUDIO.manager, `invited ${company}`, "bidder");
    });
  },
  /** Issue a fresh secure code — the old one stops working immediately and
      the bidder gets an update email with the new one. */
  reissueBidderCode(tid: string, bidderId: string): string | null {
    const t = state.tenders.find((x) => x.id === tid);
    const b = t?.bidders.find((x) => x.id === bidderId);
    if (!t || !b) return null;
    const code = secureAccessCode(t.code.split("-")[1]?.slice(0, 2) ?? "TR");
    patchTender(tid, (x) => {
      const alert: EmailAlert = {
        id: uid("al-"), audience: b.id, audienceLabel: b.company, to: [b.email],
        subject: `Your access code was updated — ${t.name} (${t.code})`,
        body: `${STUDIO.name}: your bidder access code for ${t.name} has been updated for security.\n\n` +
          `New access code: ${code}\nThe previous code no longer works.\n\n` +
          `Sign in at ${APP_URL} — your room, documents and any saved submission progress are unchanged.`,
        trigger: "invite", sentAt: nowISO(),
      };
      trackDelivery(tid, alert.id, notify([b.email], alert.subject, alert.body));
      const next: Tender = {
        ...x,
        bidders: x.bidders.map((y) => (y.id === bidderId ? { ...y, accessCode: code } : y)),
        alerts: [alert, ...x.alerts],
      };
      return logActivity(next, STUDIO.manager, `reissued ${b.company}'s access code`, "bidder");
    });
    return code;
  },
  registerBidder(tid: string, bidderId: string) {
    patchTender(tid, (t) => ({
      ...t,
      bidders: t.bidders.map((b) => b.id === bidderId && b.status === "invited" ? { ...b, status: "registered" as const, lastActive: nowISO() } : b),
    }));
  },
  declineBidder(tid: string, bidderId: string) {
    patchTender(tid, (t) => {
      const b = bidderById(t, bidderId);
      const next: Tender = { ...t, bidders: t.bidders.map((x) => x.id === bidderId ? { ...x, status: "declined" as const } : x) };
      return logActivity(next, b?.company ?? "Bidder", "declined to bid", "bidder");
    });
  },
  touchBidder(tid: string, bidderId: string) {
    patchTender(tid, (t) => ({ ...t, bidders: t.bidders.map((b) => b.id === bidderId ? { ...b, lastActive: nowISO() } : b) }));
  },

  /* --- RFIs -------------------------------------------------------------- */
  askRfi(tid: string, bidderId: string, subject: string, question: string, docId?: string, markups?: Markup[], askedVisibility: "private" | "public" = "private") {
    patchTender(tid, (t) => {
      const ref = `RFI-${String(t.rfis.length + 1).padStart(3, "0")}`;
      const r: Rfi = { id: uid("rfi-"), ref, bidderId, subject, question, askedAt: nowISO(), status: "open", visibility: "all", askedVisibility, docId, markups: markups?.length ? markups : undefined };
      const b = bidderById(t, bidderId);
      const onSheet = docId ? ` on ${t.docs.find((d) => d.id === docId)?.number ?? "a sheet"}` : "";
      notify(STUDIO.managerEmail,
        `${ref} raised${onSheet} — ${subject} (${t.code})`,
        `${b?.company ?? "A bidder"} asks (${askedVisibility === "public" ? "asked PUBLICLY — visible to all bidders, anonymised" : "asked PRIVATELY — between them and the studio"}): ${question}`);
      return logActivity({ ...t, rfis: [...t.rfis, r] }, b?.company ?? "Bidder", `raised ${ref}${onSheet} — ${subject}`, "rfi");
    });
  },
  answerRfi(tid: string, rfiId: string, answer: string, visibility: "all" | "private") {
    patchTender(tid, (t) => {
      const rfi = t.rfis.find((r) => r.id === rfiId);
      if (!rfi) return t;
      const next: Tender = { ...t, rfis: t.rfis.map((r) => r.id === rfiId ? { ...r, answer, answeredAt: nowISO(), status: "answered" as const, visibility } : r) };
      const audience: AlertAudience = visibility === "all" ? "bidders" : rfi.bidderId;
      const { label, to } = resolveAudience(next, audience);
      const alert: EmailAlert = {
        id: uid("al-"), audience, audienceLabel: label, to,
        subject: `${rfi.ref} answered — ${rfi.subject} (${t.code})`,
        body: visibility === "all"
          ? `A clarification has been issued to all bidders. ${rfi.ref}: ${answer}`
          : `Your query ${rfi.ref} has been answered: ${answer}`,
        trigger: "rfi-answered", sentAt: nowISO(),
      };
      trackDelivery(tid, alert.id, notify(to, alert.subject, alert.body));
      return logActivity({ ...next, alerts: [alert, ...next.alerts] }, STUDIO.manager, `answered ${rfi.ref}${visibility === "all" ? " — circulated to all bidders" : ""}`, "rfi");
    });
  },

  /* --- bids --------------------------------------------------------------- */
  /* --- invitation to tender ------------------------------------------------ */
  setItt(tid: string, itt: InvitationToTender, issue = false) {
    patchTender(tid, (t) => {
      const next: Tender = { ...t, itt: { ...itt, updatedAt: nowISO(), issuedAt: issue ? nowISO() : itt.issuedAt } };
      return logActivity(next, STUDIO.manager, issue ? "issued the Invitation to Tender to all bidders" : "updated the Invitation to Tender", "docs");
    });
  },

  submitBid(tid: string, bidderId: string, data: { amount: number; durationMonths: number; bond: boolean; note?: string; attachments: string[]; checklist?: BidChecklistItem[] }) {
    patchTender(tid, (t) => {
      const ack = t.docs.filter((d) => d.category === "addendum").length;
      const existing = t.bids.find((b) => b.bidderId === bidderId);
      const bid: Bid = {
        id: existing?.id ?? uid("bid-"), bidderId,
        amount: data.amount, currency: t.currency, durationMonths: data.durationMonths,
        submittedAt: nowISO(), bond: data.bond, addendaAck: ack, note: data.note,
        attachments: data.attachments, checklist: data.checklist, compliance: "pending", status: "received",
      };
      const b = bidderById(t, bidderId);
      const next: Tender = {
        ...t,
        bids: existing ? t.bids.map((x) => x.id === existing.id ? bid : x) : [...t.bids, bid],
        bidders: t.bidders.map((x) => x.id === bidderId ? { ...x, status: "submitted" as const, lastActive: nowISO() } : x),
      };
      notify(STUDIO.managerEmail,
        `${t.bidMode === "open" ? "Bid" : "Sealed bid"} ${existing ? "revised" : "received"} — ${b?.company} (${t.code})`,
        t.bidMode === "open"
          ? `${b?.company} ${existing ? "revised their" : "submitted a"} bid for ${t.name}. This is an open tender — the amount is visible in Bid Results now.`
          : `${b?.company} ${existing ? "revised their" : "submitted a"} sealed bid for ${t.name}. Amounts stay sealed until opening.`);
      return logActivity(next, b?.company ?? "Bidder", existing ? "revised their sealed bid" : "submitted a sealed bid", "bid");
    });
  },
  openBids(tid: string) {
    patchTender(tid, (t) => {
      const next: Tender = { ...t, status: "evaluation" };
      return logActivity(next, STUDIO.manager, `opened bids — ${t.bids.length} received, results logged`, "bid");
    });
  },
  /** Put a closed or under-evaluation tender back into bidding.

      `openBids` was a one-way door: it moves the status to "evaluation", and
      because `isClosed` reads BOTH the close date and the status, extending the
      date alone could never bring the tender back. This is the way back.

      Reopening after submissions have been seen is a serious procurement act,
      so it is never silent: the reason is required, every active bidder is
      told what happened, and it goes on the record in plain words. */
  reopenBidding(tid: string, closeDate: string, reason: string) {
    patchTender(tid, (t) => {
      // Still open → the deadline is simply moving. Closed/evaluated/awarded → the
      // tender is genuinely being reopened, which reads very differently to a bidder.
      const extending = t.status === "open";
      const wasUnsealed = !extending && bidsUnsealed(t);
      let next: Tender = {
        ...t,
        status: "open",
        closeDate,
        timeline: t.timeline.map((e) => (e.kind === "close" ? { ...e, date: closeDate } : e))
          .sort((a, b) => +new Date(a.date) - +new Date(b.date)),
      };
      if (activeBidders(t).length) {
        const { label, to } = resolveAudience(t, "bidders");
        const alert: EmailAlert = {
          id: uid("al-"), audience: "bidders", audienceLabel: label, to,
          subject: extending
            ? `Closing date extended — ${t.name} (${t.code})`
            : `Tender reopened for bidding — ${t.name} (${t.code})`,
          body: (extending
            ? `The closing date for ${t.name} has been extended. Submissions are now due by ${new Date(closeDate).toLocaleString()}.\n\n`
            : `${t.name} has been reopened for bidding. Submissions are now due by ${new Date(closeDate).toLocaleString()}.\n\n`)
            + `Reason: ${reason}\n\n`
            + (wasUnsealed
              ? `Please note that submissions received before this point had already been opened. You may revise your submission before the new closing time.`
              : `All other conditions of the tender are unchanged.`),
          trigger: "deadline", sentAt: nowISO(),
        };
        trackDelivery(tid, alert.id, notify(to, alert.subject, alert.body));
        next = { ...next, alerts: [alert, ...next.alerts] };
      }
      return logActivity(next, STUDIO.manager,
        `${extending ? "extended the closing date to" : "reopened bidding to"} ${new Date(closeDate).toLocaleString()}`
        + `${wasUnsealed ? " — bids had already been opened" : ""}: ${reason}`,
        "timeline");
    });
  },

  /** Switch the tender's bidding procedure while it's still running. Active
      bidders are formally notified — the rules of the game are theirs too. */
  setBidMode(tid: string, mode: "sealed" | "open") {
    patchTender(tid, (t) => {
      if (t.status !== "open" || (t.bidMode ?? "sealed") === mode) return t;
      if (mode === "sealed" && t.bids.length > 0) return t;   // can't re-seal amounts already seen
      let next: Tender = { ...t, bidMode: mode };
      if (activeBidders(t).length) {
        const { label, to } = resolveAudience(t, "bidders");
        const alert: EmailAlert = {
          id: uid("al-"), audience: "bidders", audienceLabel: label, to,
          subject: `Bidding procedure updated — ${t.name} (${t.code})`,
          body: mode === "open"
            ? `This tender now runs as OPEN bidding: submitted amounts are visible to the tender manager on arrival. All other conditions are unchanged.`
            : `This tender now runs as SEALED bidding: submitted amounts stay hidden from everyone, including the manager, until the formal opening after close.`,
          trigger: "manual", sentAt: nowISO(),
        };
        trackDelivery(tid, alert.id, notify(to, alert.subject, alert.body));
        next = { ...next, alerts: [alert, ...next.alerts] };
      }
      return logActivity(next, STUDIO.manager, `switched to ${mode} bidding`, "bid");
    });
  },
  setCompliance(tid: string, bidId: string, compliance: Compliance) {
    patchTender(tid, (t) => ({ ...t, bids: t.bids.map((b) => b.id === bidId ? { ...b, compliance } : b) }));
  },
  setScore(tid: string, bidId: string, score: number | undefined) {
    patchTender(tid, (t) => ({ ...t, bids: t.bids.map((b) => b.id === bidId ? { ...b, score } : b) }));
  },
  toggleShortlist(tid: string, bidId: string) {
    patchTender(tid, (t) => ({
      ...t,
      bids: t.bids.map((b) => b.id === bidId
        ? { ...b, status: b.status === "shortlisted" ? "received" as const : "shortlisted" as const }
        : b),
    }));
  },
  award(tid: string, bidId: string) {
    patchTender(tid, (t) => {
      const winner = t.bids.find((b) => b.id === bidId);
      if (!winner) return t;
      const wb = bidderById(t, winner.bidderId);
      if (wb) directoryActions.learn({ name: wb.company, email: wb.email, kind: "contractor", scopeTitle: t.name, event: { role: "tender-awarded", ref: t.code, label: t.name } });
      const { label, to } = resolveAudience(t, "bidders");
      const alert: EmailAlert = {
        id: uid("al-"), audience: "bidders", audienceLabel: label, to,
        subject: `Notice of award — ${t.name} (${t.code})`,
        body: `The Employer intends to award the contract for ${t.name} to ${wb?.company ?? "the successful bidder"}. All bidders are thanked for their submissions.`,
        trigger: "award", sentAt: nowISO(),
      };
      const clientAlert: EmailAlert = {
        id: uid("al-"), audience: "client", audienceLabel: `Client — ${t.client.company}`, to: [t.client.email],
        subject: `Recommendation accepted — award to ${wb?.company} (${t.code})`,
        body: `The award notice has been issued to ${wb?.company}. Contract signing to follow per the tender programme.`,
        trigger: "award", sentAt: nowISO(),
      };
      const next: Tender = {
        ...t,
        status: "awarded",
        awardedBidderId: winner.bidderId,
        bids: t.bids.map((b) => b.id === bidId ? { ...b, status: "awarded" as const } : { ...b, status: "unsuccessful" as const }),
        alerts: [alert, clientAlert, ...t.alerts],
      };
      trackDelivery(tid, alert.id, notify(alert.to, alert.subject, alert.body));
      trackDelivery(tid, clientAlert.id, notify(clientAlert.to, clientAlert.subject, clientAlert.body));
      return logActivity(next, STUDIO.manager, `awarded to ${wb?.company}`, "award");
    });
  },
  shareResults(tid: string, on: boolean) {
    patchTender(tid, (t) => logActivity({ ...t, resultsSharedWithClient: on }, STUDIO.manager, on ? "shared bid results with the client" : "hid bid results from the client", "bid"));
  },

  /* --- contract ------------------------------------------------------------ */
  importContract(tid: string, draft: DraftContract) {
    // a re-import refreshes the particulars but keeps any uploaded documents
    patchTender(tid, (t) => logActivity({ ...t, contract: { ...draft, files: t.contract?.files } }, STUDIO.manager, "imported draft contract from Contract Generator", "contract"));
  },
  removeContract(tid: string) {
    patchTender(tid, (t) => logActivity({ ...t, contract: undefined }, STUDIO.manager, "removed the draft contract", "contract"));
  },
  /** Attach an uploaded contract document — bidders and the client download it
      from their Contract Terms tab. */
  attachContractFile(tid: string, f: { name: string; size?: string; fileId: string }) {
    patchTender(tid, (t) => {
      if (!t.contract) return t;
      const row: ContractFile = { id: uid("cf-"), uploadedAt: nowISO(), ...f };
      return logActivity(
        { ...t, contract: { ...t.contract, files: [...(t.contract.files ?? []), row] } },
        STUDIO.manager, `attached contract document “${f.name}”`, "contract",
      );
    });
  },
  removeContractFile(tid: string, id: string) {
    patchTender(tid, (t) => t.contract
      ? { ...t, contract: { ...t.contract, files: (t.contract.files ?? []).filter((x) => x.id !== id) } }
      : t);
  },

  /* --- specifications (Building Systems Overview) --------------------------- */
  /** Import a CSI specification set: one specification document per division in
      the register (revised in place on re-import), plus the written narrative
      into the ITT specifications section. */
  importSpecs(tid: string, specs: ParsedSpecs) {
    const bump = (rev: string) => { const m = rev.match(/^([A-Za-z-]*?)(\d+)$/); return m ? m[1] + (+m[2] + 1) : rev + "-1"; };
    patchTender(tid, (t) => {
      const docs = [...t.docs];
      for (const d of specs.divisions) {
        const number = `SPEC ${d.no}`;
        const title = `${d.no} ${d.name}`;
        const size = `${d.sections.length} sec`;
        const i = docs.findIndex((x) => x.category === "specification" && x.number.toUpperCase() === number.toUpperCase());
        if (i >= 0) docs[i] = { ...docs[i], title, discipline: "Specifications", revision: bump(docs[i].revision), size, issuedAt: nowISO() };
        else docs.push({ id: uid("doc-"), number, title, discipline: "Specifications", revision: "P1", category: "specification", size, issuedAt: nowISO() });
      }
      const base = t.itt ? normalizeItt(t.itt) : emptyItt();
      const stamp = `[Imported from Building Systems Overview - ${specs.projectName}${specs.generatedAt ? ` - ${specs.generatedAt.slice(0, 10)}` : ""}]`;
      const itt: InvitationToTender = {
        ...base,
        sections: base.sections.map((s) => (s.key === "specifications" ? { ...s, body: `${stamp}\n\n${specs.narrative}` } : s)),
        updatedAt: nowISO(),
      };
      const total = specs.divisions.reduce((n, d) => n + d.sections.length, 0);
      return logActivity({ ...t, docs, itt }, STUDIO.manager, `imported CSI specifications (${specs.divisions.length} divisions, ${total} sections) from Building Systems Overview`, "docs");
    });
  },
};
